import { Anima } from "kido/anima";
import { clamp, damp, lerp, queryAll } from "kido/utils";

import { App } from "@app/context";
import type Scene from "@app/gpu/scene";
import { BasePage } from "@app/primitives/base-page";

export class PlayDetailPage extends BasePage {
  private scene: Scene | null = null;
  private slug: string | null = null;
  private strip: HTMLElement | null = null;

  // Horizontal scroll state
  private _scrollTarget = 0;
  private _scrollCurrent = 0;
  private _scrollMax = 0;
  private _stripOffset = 0;
  private _onWheel: ((e: WheelEvent) => void) | null = null;

  // Arrow key scroll
  private _onKeyDown: ((e: KeyboardEvent) => void) | null = null;

  // Drag-to-scroll state
  private _isDragging = false;
  private _dragLastX = 0;
  private _dragVel = 0;

  // Whether the horizontal strip layout is active (viewport >= CSS `md`)
  private _horizontal = false;
  private _dragBound = {
    down: (e: PointerEvent) => this._onDragDown(e),
    move: (e: PointerEvent) => this._onDragMove(e),
    up: () => this._onDragUp(),
  };
  private _rafId = 0;

  // Track whether we came from play grid (for morph animation)
  private _fromPlay = false;

  constructor() {
    super();
  }

  async init(container: Element | null): Promise<void> {
    if (!container) return;
    await super.init(container);

    // Horizontal-layout widths (CSS `md` and up): wheel, arrow-key, and drag
    // scroll. Below that the CSS stacks vertically and native scroll runs.
    const vw = window.innerWidth || document.documentElement.clientWidth;
    this._horizontal = vw >= 1000;

    if (!App.gpu && !this._horizontal) return;

    const url = App.route.new.url ?? "";
    this.slug = url.split("/").pop() ?? null;

    this.strip = (container as HTMLElement).querySelector(".ply-strip");

    if (this._horizontal) {
      App.scroller?.pause?.();
      document.documentElement.style.overflow = "hidden";

      if (this.strip) {
        this._stripOffset = this.strip.getBoundingClientRect().left;
        this._scrollMax = Math.max(
          0,
          this.strip.scrollWidth - window.innerWidth + this._stripOffset
        );
        this._scrollTarget = 0;
        this._scrollCurrent = 0;

        this._onWheel = (e: WheelEvent) => {
          if (e.ctrlKey) return;
          e.preventDefault();
          let delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
          if (e.deltaMode === 1) delta *= 40;
          if (e.deltaMode === 2) delta *= 800;
          this._scrollTarget = clamp(
            this._scrollTarget + delta,
            0,
            this._scrollMax
          );
        };
        window.addEventListener("wheel", this._onWheel, { passive: false });

        const ARROW_STEP = 200;
        this._onKeyDown = (e: KeyboardEvent) => {
          const key = e.key;
          if (key === "ArrowRight" || key === "ArrowUp") {
            e.preventDefault();
            this._scrollTarget = clamp(
              this._scrollTarget + ARROW_STEP,
              0,
              this._scrollMax
            );
            App.gpu?.requestExtraFrames?.();
          } else if (key === "ArrowLeft" || key === "ArrowDown") {
            e.preventDefault();
            this._scrollTarget = clamp(
              this._scrollTarget - ARROW_STEP,
              0,
              this._scrollMax
            );
            App.gpu?.requestExtraFrames?.();
          }
        };
        window.addEventListener("keydown", this._onKeyDown);

        this.strip.addEventListener("pointerdown", this._dragBound.down);
        window.addEventListener("pointermove", this._dragBound.move);
        window.addEventListener("pointerup", this._dragBound.up);
        this.strip.style.cursor = "grab";
        // Without this, iPad Safari cancels pointer events to attempt native
        // scrolling and the drag never moves the strip
        this.strip.style.touchAction = "none";
      }
    }

    if (App.gpu) {
      // Get the shared /play scene
      const sceneKey = "/play";
      this.scene =
        (App.gpu.scenes as Record<string, Scene>)?.[sceneKey] ?? null;

      if (this.scene) {
        this.scene.restorePlanes();
        const ts = App.transitState;
        this.scene.injectProjectMedia(url, ts?.planeId);
      }

      // Check if coming from play grid with cover bounds for morph
      const ts = App.transitState;
      this._fromPlay = !!(
        ts?.direction === "to-play-detail" &&
        ts.coverBounds &&
        this.scene &&
        App.gpu
      );

      if (this._fromPlay && App.gpu) {
        App.gpu.freezeBounds?.(true);

        // Position cover plane at its old grid bounds
        const coverPlane = this.scene!.planes[0];
        if (coverPlane && ts?.coverBounds) {
          const cb = ts.coverBounds;
          if (!coverPlane.boundsPx) {
            coverPlane.boundsPx = { x: 0, y: 0, w: 0, h: 0, z: 1 };
          }
          coverPlane.boundsPx.x = cb.x;
          coverPlane.boundsPx.y = cb.y;
          coverPlane.boundsPx.w = cb.w;
          coverPlane.boundsPx.h = cb.h;
        }

        // Hide all non-cover planes
        const scenePlanes = this.scene!.planes;
        for (let i = 1; i < scenePlanes.length; i++) {
          const p = scenePlanes[i];
          if (p) p.opacity = 0;
        }

        // Hide all non-cover items (media blocks, text, play buttons)
        if (this.strip) {
          const items = queryAll(this.strip, ".ply-item");
          for (const item of items) {
            if (!(item as HTMLElement).classList.contains("ply-cover")) {
              (item as HTMLElement).style.opacity = "0";
            }
          }
          // Also hide text/play inside the cover item
          const coverTexts = queryAll(
            this.strip,
            ".ply-cover .ply-text, .ply-cover .ply-play"
          );
          for (const el of coverTexts) {
            (el as HTMLElement).style.opacity = "0";
          }
        }
      }

      if (!this._fromPlay) {
        App.gpu.requestBoundsUpdate?.();
        App.gpu.requestRedraw?.();
      }
    }
  }

  private _loop(): void {
    if (!this.strip) return;

    const max = Math.max(
      0,
      this.strip.scrollWidth - window.innerWidth + this._stripOffset
    );
    if (max !== this._scrollMax) {
      this._scrollMax = max;
      this._scrollTarget = clamp(this._scrollTarget, 0, max);
    }

    this._scrollCurrent = damp(this._scrollCurrent, this._scrollTarget, 0.18);
    this.strip.style.transform = `translate3d(${-this._scrollCurrent}px, 0, 0)`;

    if (Math.abs(this._scrollCurrent - this._scrollTarget) > 0.5) {
      App.gpu?.requestExtraFrames?.();
    }
  }

  private _onDragDown(e: PointerEvent): void {
    this._isDragging = true;
    this._dragLastX = e.clientX;
    this._dragVel = 0;
    // Landing a pointer stops any in-flight glide
    this._scrollTarget = clamp(this._scrollCurrent, 0, this._scrollMax);
    if (this.strip) this.strip.style.cursor = "grabbing";
  }

  private _onDragMove(e: PointerEvent): void {
    if (!this._isDragging) return;
    const delta = this._dragLastX - e.clientX;
    this._dragLastX = e.clientX;
    this._dragVel = delta;
    this._scrollTarget = clamp(this._scrollTarget + delta, 0, this._scrollMax);
    App.gpu?.requestExtraFrames?.();
  }

  private _onDragUp(): void {
    if (!this._isDragging) return;
    this._isDragging = false;
    // Flick momentum: project the last frame's velocity forward
    this._scrollTarget = clamp(
      this._scrollTarget + this._dragVel * 15,
      0,
      this._scrollMax
    );
    this._dragVel = 0;
    App.gpu?.requestExtraFrames?.();
    if (this.strip) this.strip.style.cursor = "grab";
  }

  private _startRafLoop(): void {
    const tick = () => {
      this._loop();
      this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  private _stopRafLoop(): void {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = 0;
    }
  }

  async in(): Promise<void> {
    const ts = App.transitState;
    App.transitState = null;

    if (!App.gpu) {
      if (this._horizontal) {
        App.cursor?.setDrag(true);
        this._startRafLoop();
      }
      return;
    }

    App.cursor?.setDrag(true);

    if (this._fromPlay && ts?.coverBounds && this.scene) {
      const coverPlane = this.scene.planes[0];
      const coverSlot = this.scene.domSlots[0] as HTMLElement | null;
      const scenePlanes = this.scene.planes;
      const sceneSlots = this.scene.domSlots;

      const morphDuration = 800;
      const staggerDelay = Math.round(morphDuration * 0.9);

      // 1. Cover morph
      if (coverPlane && coverSlot) {
        const targetRect = coverSlot.getBoundingClientRect();
        const startBounds = ts.coverBounds;

        new Anima({
          d: morphDuration,
          e: "ioW",
          u: (v) => {
            const t = v.prE;
            if (coverPlane.boundsPx) {
              coverPlane.boundsPx.x = lerp(startBounds.x, targetRect.x, t);
              coverPlane.boundsPx.y = lerp(startBounds.y, targetRect.y, t);
              coverPlane.boundsPx.w = lerp(startBounds.w, targetRect.width, t);
              coverPlane.boundsPx.h = lerp(startBounds.h, targetRect.height, t);
            }
            App.gpu?.requestRedraw?.();
          },
          cb: () => {
            if (coverPlane.boundsPx) {
              coverPlane.boundsPx.x = targetRect.x;
              coverPlane.boundsPx.y = targetRect.y;
              coverPlane.boundsPx.w = targetRect.width;
              coverPlane.boundsPx.h = targetRect.height;
            }
            App.gpu?.freezeBounds?.(false);
          },
        }).play();
      }

      // Fade in cover text/play button at morph ~90%
      if (this.strip) {
        const coverEls = queryAll(
          this.strip,
          ".ply-cover .ply-text, .ply-cover .ply-play"
        );
        for (const el of coverEls) {
          new Anima({
            el: el as HTMLElement,
            p: { opacity: [0, 1] },
            d: 600,
            e: "oQ",
            de: staggerDelay,
          }).play();
        }
      }

      // 2. Stagger in non-cover items + planes
      const items = this.strip
        ? queryAll(this.strip, ".ply-item:not(.ply-cover)")
        : [];

      const baseStagger = 80;
      const staggerDecay = 0.65;
      const getDelay = (s: number): number => {
        if (s <= 0) return 0;
        return (
          (baseStagger * (1 - Math.pow(staggerDecay, s))) / (1 - staggerDecay)
        );
      };

      const staggerDone = new Promise<void>((resolve) => {
        if (!items.length) {
          resolve();
          return;
        }
        let completed = 0;

        for (let s = 0; s < items.length; s++) {
          const item = items[s] as HTMLElement;
          const delay = staggerDelay + getDelay(s);

          // Find GPU planes within this item and fade them in
          const figures = queryAll(item, ".g__l");
          for (const fig of figures) {
            const planeId = (fig as HTMLElement).getAttribute("data-plane");
            if (!planeId) continue;
            for (let p = 0; p < sceneSlots.length; p++) {
              const slot = sceneSlots[p] as HTMLElement | null;
              if (
                slot?.getAttribute("data-plane") === planeId &&
                scenePlanes[p]
              ) {
                const plane = scenePlanes[p]!;
                new Anima({
                  d: 800,
                  e: "oQ",
                  de: delay,
                  u: (v) => {
                    plane.opacity = v.prE;
                    App.gpu?.requestRedraw?.();
                  },
                  cb: () => {
                    plane.opacity = 1;
                  },
                }).play();
              }
            }
          }

          // Fade in DOM item
          new Anima({
            el: item,
            p: { opacity: [0, 1] },
            d: 800,
            e: "oQ",
            de: delay,
            cb: () => {
              completed++;
              if (completed >= items.length) resolve();
            },
          }).play();
        }
      });

      await staggerDone;
    } else {
      // Normal entry (not from play)
      App.gpu.freezeBounds?.(false);
    }

    App.gpu.setPreRender?.(() => this._loop());
  }

  async out(): Promise<void> {
    if (this._horizontal) {
      App.cursor?.setDrag(false);
      this._removeScrollListeners();

      if (App.gpu) {
        App.gpu.setPreRender?.(null);
      } else {
        this._stopRafLoop();
      }

      App.scroller?.resume?.();
      document.documentElement.style.overflow = "";
    }

    if (this.scene) {
      const goingToPlay = App.route.new.url === "/play";
      if (goingToPlay && this.slug) {
        const coverPlane = this.scene.planes[0];
        const coverBounds = coverPlane?.boundsPx
          ? {
              x: coverPlane.boundsPx.x,
              y: coverPlane.boundsPx.y,
              w: coverPlane.boundsPx.w,
              h: coverPlane.boundsPx.h,
            }
          : null;
        if (coverBounds) {
          App.transitState = {
            coverBounds,
            slug: this.slug,
            planeId: coverPlane?.id,
            direction: "to-play",
          };
        }
      }
    }

    this._destroy();
  }

  private _removeScrollListeners(): void {
    if (this._onWheel) {
      window.removeEventListener("wheel", this._onWheel);
      this._onWheel = null;
    }
    if (this._onKeyDown) {
      window.removeEventListener("keydown", this._onKeyDown);
      this._onKeyDown = null;
    }
    this._isDragging = false;
    if (this.strip) {
      this.strip.removeEventListener("pointerdown", this._dragBound.down);
    }
    window.removeEventListener("pointermove", this._dragBound.move);
    window.removeEventListener("pointerup", this._dragBound.up);
  }

  private _destroy(): void {
    this.strip = null;
    this._fromPlay = false;
    this.scene = null;
    this.slug = null;
    this._onKeyDown = null;
    this._dragVel = 0;
    this._horizontal = false;
    this._scrollTarget = 0;
    this._scrollCurrent = 0;
    this._scrollMax = 0;
    this._stripOffset = 0;
  }
}
